import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { progressAPI, planAPI } from '../utils/api';
import { translateReference } from '../utils/translateReferences';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BibleReader from '../components/BibleReader';
import SEO from '../components/SEO';
import { CheckCircle, Circle, BookOpen, Calendar, AlertCircle, Target } from 'lucide-react';
import './ReadingPlan.css';

const ReadingPlan = () => {
  const { user } = useAuth();
  const [progress, setProgress] = useState({});
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('english');
  const [missedDays, setMissedDays] = useState([]);
  const [showMissedDaysWarning, setShowMissedDaysWarning] = useState(false);

  const checkMissedDays = useCallback(async (statusData) => {
    try {
      const progressResponse = await progressAPI.getAllProgress();
      const allProgress = progressResponse.data.progress;
      
      const missed = [];
      for (let day = 1; day < statusData.currentDay; day++) {
        if (!allProgress[day] || !allProgress[day].completedAt) {
          missed.push(day);
        }
      }
      
      setMissedDays(missed);
      setShowMissedDaysWarning(missed.length > 0);
    } catch (err) {
      console.error('Error checking missed days:', err);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [progressRes, statusRes, planRes] = await Promise.all([
        progressAPI.getAllProgress(),
        progressAPI.getStatus(),
        planAPI.getAll(),
      ]);
      setProgress(progressRes.data.progress);
      setStatus(statusRes.data);
      setFullPlan(planRes.data.plan || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    if (user?.languagePreference === 'telugu') {
      setSelectedLanguage('telugu');
    }
  }, []);

  useEffect(() => {
    if (status && status.started) {
      checkMissedDays(status);
    }
  }, [status, checkMissedDays]);

  const handleCompleteDay = async (dayNumber) => {
    setCompleting(dayNumber);
    try {
      await progressAPI.completeDay(dayNumber);
      await fetchData();
    } catch (err) {
      console.error('Error completing day:', err);
    } finally {
      setCompleting(null);
    }
  };

  const [fullPlan, setFullPlan] = React.useState([]);
  const [expandedDay, setExpandedDay] = React.useState(null);

  const toggleDay = (dayNumber) => {
    setExpandedDay(expandedDay === dayNumber ? null : dayNumber);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container">
          <div className="loading-spinner">Loading reading plan...</div>
        </div>
      </>
    );
  }

  const readingPlanStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Complete 365-Day Bible Reading Plan - HolyBible365",
  "description": "View the complete 365-day Bible reading plan with daily passages from Old Testament, New Testament, Psalms & Proverbs. Track progress and catch up on missed readings.",
  "url": "https://holybible365.com/plan",
  "isPartOf": {
    "@type": "WebSite",
    "name": "HolyBible365",
    "url": "https://holybible365.com"
  },
  "mainEntity": {
    "@type": "Course",
    "name": "365-Day Bible Reading Plan",
    "description": "Complete structured reading plan covering the entire Bible in 365 days",
    "educationalLevel": "Beginner to Advanced",
    "inLanguage": ["en", "te"],
    "timeRequired": "P365D",
    "provider": {
      "@type": "Organization",
      "name": "HolyBible365"
    }
  }
};

return (
    <>
      <SEO 
        title="Complete 365-Day Bible Reading Plan"
        description="View the complete 365-day Bible reading plan with daily passages from Old Testament, New Testament, Psalms & Proverbs. Available in English and Telugu with progress tracking."
        keywords="365 day Bible plan, complete Bible reading, Bible study schedule, Old Testament New Testament, Christian reading plan, Telugu Bible plan"
        canonicalUrl="https://holybible365.com/plan"
        structuredData={readingPlanStructuredData}
      />
      <Navbar />
      <div className="plan-container">
        <div className="container">
          <div className="plan-header card">
            <div className="plan-header-top">
              <Calendar size={48} />
              <div className="language-selector">
                <button 
                  className={`lang-btn ${selectedLanguage === 'english' ? 'active' : ''}`}
                  onClick={() => setSelectedLanguage('english')}
                >
                  English
                </button>
                <button 
                  className={`lang-btn ${selectedLanguage === 'telugu' ? 'active' : ''}`}
                  onClick={() => setSelectedLanguage('telugu')}
                >
                  తెలుగు
                </button>
              </div>
            </div>
            <h1>{selectedLanguage === 'telugu' ? 'బైబిల్ పఠన ప్రణాళిక - 365 రోజులు' : 'Bible Reading Plan - 365 Days'}</h1>
            <p>{selectedLanguage === 'telugu' ? 'ఒక సంవత్సరంలో మొత్తం బైబిల్ ద్వారా మీ ప్రయాణాన్ని పూర్తి చేయండి' : 'Complete your journey through the entire Bible in one year'}</p>
            {status?.started && (
              <div className="plan-stats">
                <span className="stat-item">
                  <strong>{status.completedDays}</strong> {selectedLanguage === 'telugu' ? 'పూర్తయింది' : 'completed'}
                </span>
                <span className="stat-item">
                  <strong>{365 - status.completedDays}</strong> {selectedLanguage === 'telugu' ? 'మిగిలి ఉంది' : 'remaining'}
                </span>
                <span className="stat-item">
                  <strong>{status.progressPercentage}%</strong> {selectedLanguage === 'telugu' ? 'పురోగతి' : 'progress'}
                </span>
              </div>
            )}
          </div>

          {/* Missed Days Warning */}
          {showMissedDaysWarning && missedDays.length > 0 && (
            <div className="plan-missed-days-warning card">
              <div className="plan-warning-header">
                <AlertCircle size={28} className="plan-warning-icon" />
                <div>
                  <h3>⚠️ {selectedLanguage === 'telugu' ? 'జాడిన రోజులు గుర్తించబడ్డాయి' : 'Missed Days Detected'}</h3>
                  <p>
                    {selectedLanguage === 'telugu' 
                      ? `మీకు ${missedDays.length} జాడిన రోజు${missedDays.length > 1 ? 'లు' : ''} ఉన్నాయి`
                      : `You have ${missedDays.length} missed day${missedDays.length > 1 ? 's' : ''}`
                    }
                  </p>
                </div>
              </div>
              
              <div className="plan-missed-days-summary">
                <div className="plan-missed-days-badges">
                  {missedDays.slice(0, 8).map(day => (
                    <span key={day} className="plan-missed-day-badge">
                      {selectedLanguage === 'telugu' ? `రోజు ${day}` : `Day ${day}`}
                    </span>
                  ))}
                  {missedDays.length > 8 && (
                    <span className="plan-missed-day-more">
                      +{missedDays.length - 8} {selectedLanguage === 'telugu' ? 'మరిన్ని' : 'more'}
                    </span>
                  )}
                </div>
              </div>

              <div className="plan-warning-actions">
                <button 
                  onClick={() => {
                    // Scroll to first missed day
                    const firstMissedDay = missedDays[0];
                    const element = document.getElementById(`day-${firstMissedDay}`);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                    setShowMissedDaysWarning(false);
                  }} 
                  className="btn btn-warning btn-medium"
                >
                  <Target size={16} />
                  {selectedLanguage === 'telugu' ? 'మొదటి జాడిన రోజుకు వెళ్ళండి' : 'Go to First Missed Day'}
                </button>
                <button 
                  onClick={() => setShowMissedDaysWarning(false)} 
                  className="btn btn-outline btn-medium"
                >
                  {selectedLanguage === 'telugu' ? 'తర్వాత గుర్తించండి' : 'Remind Later'}
                </button>
              </div>
            </div>
          )}

          <div className="plan-grid">
            {fullPlan.map((day) => {
              const isCompleted = progress[day.day]?.completed;
              const isCurrent = status?.currentDay === day.day;
              const isExpanded = expandedDay === day.day;
              const isMissed = !isCompleted && day.day < status?.currentDay;

              return (
                <div 
                  key={day.day} 
                  id={`day-${day.day}`}
                  className={`plan-card card ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''} ${isMissed ? 'missed' : ''}`}
                >
                  <div 
                    className="plan-card-header clickable"
                    onClick={() => toggleDay(day.day)}
                  >
                    <div className="day-info">
                      <h3>{selectedLanguage === 'telugu' ? `రోజు ${day.day}` : `Day ${day.day}`}</h3>
                      {isCurrent && <span className="current-badge">{selectedLanguage === 'telugu' ? 'ఈ రోజు' : 'Today'}</span>}
                      {isMissed && <span className="missed-badge">{selectedLanguage === 'telugu' ? 'జాడింది' : 'Missed'}</span>}
                    </div>
                    <div className="completion-icon">
                      {isCompleted ? (
                        <CheckCircle size={28} className="check-icon completed" />
                      ) : isMissed ? (
                        <AlertCircle size={28} className="alert-icon missed" />
                      ) : (
                        <Circle size={28} className="circle-icon pending" />
                      )}
                    </div>
                  </div>

                  <div className="plan-content">
                    {isExpanded && (
                      <>
                        <BibleReader 
                          readingText={day.oldTestament} 
                          title="Old Testament"
                          selectedLanguage={selectedLanguage}
                        />
                        <BibleReader 
                          readingText={day.newTestament} 
                          title="New Testament"
                          selectedLanguage={selectedLanguage}
                        />
                        <BibleReader 
                          readingText={day.psalmsProverbs} 
                          title="Psalms & Proverbs"
                          selectedLanguage={selectedLanguage}
                        />
                      </>
                    )}
                    {!isExpanded && (
                      <>
                        <div className="reading-item">
                          <BookOpen size={16} />
                          <div>
                            <strong className={selectedLanguage === 'telugu' ? 'telugu-text' : ''}>
                              {selectedLanguage === 'telugu' ? 'పాత నిబంధన:' : 'Old Testament:'}
                            </strong>
                            <p className={selectedLanguage === 'telugu' ? 'telugu-text' : ''}>
                              {translateReference(day.oldTestament, selectedLanguage === 'telugu')}
                            </p>
                          </div>
                        </div>
                        <div className="reading-item">
                          <BookOpen size={16} />
                          <div>
                            <strong className={selectedLanguage === 'telugu' ? 'telugu-text' : ''}>
                              {selectedLanguage === 'telugu' ? 'కొత్త నిబంధన:' : 'New Testament:'}
                            </strong>
                            <p className={selectedLanguage === 'telugu' ? 'telugu-text' : ''}>
                              {translateReference(day.newTestament, selectedLanguage === 'telugu')}
                            </p>
                          </div>
                        </div>
                        <div className="reading-item">
                          <BookOpen size={16} />
                          <div>
                            <strong className={selectedLanguage === 'telugu' ? 'telugu-text' : ''}>
                              {selectedLanguage === 'telugu' ? 'కీర్తనలు & సామెతలు:' : 'Psalms & Proverbs:'}
                            </strong>
                            <p className={selectedLanguage === 'telugu' ? 'telugu-text' : ''}>
                              {translateReference(day.psalmsProverbs, selectedLanguage === 'telugu')}
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {status?.started && !isCompleted && (
                    <button
                      onClick={() => handleCompleteDay(day.day)}
                      className="btn btn-success btn-small"
                      disabled={completing === day.day}
                    >
                      {completing === day.day 
                        ? (selectedLanguage === 'telugu' ? 'పూర్తి చేస్తోంది...' : 'Completing...') 
                        : (selectedLanguage === 'telugu' ? 'పూర్తయినట్లు గుర్తించండి' : 'Mark as Completed')}
                    </button>
                  )}

                  {isCompleted && progress[day.day]?.completedAt && (
                    <p className="completed-date">
                      {selectedLanguage === 'telugu' ? 'పూర్తయింది: ' : 'Completed: '}
                      {new Date(progress[day.day].completedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ReadingPlan;
